/**
 * Session-scoped browser persistence for the client offer review's in-progress
 * decisions, selected periods, and granularity — client-offer-interactive-view /
 * "Client Review Selections Survive Navigation Away From The Offer Page".
 *
 * Since the unit detail affordance now navigates to a separate route
 * (client-media-unit-detail), `ClientOfferReviewShell` unmounts during a
 * normal review. Persisting to `sessionStorage` (never `localStorage`) lets
 * `useClientOfferSelections` restore the review when the shell remounts —
 * after a unit detail round trip or a page reload — without resurrecting a
 * stale review across tab-close, a different offer/token, or a superseded
 * version.
 *
 * Key shape: `ooh:client-offer-selections:<scope>:<scopeId>:<versionId>`,
 * mirroring the `ooh:`-prefixed storage-key convention (see
 * `units-tab-columns.tsx`'s `OFFER_TABLE_COLUMN_STORAGE_PREFIX`).
 *
 * All reads/writes/clears are wrapped in try/catch and fail silently (no
 * console output) — storage being disabled, full, or throwing for any
 * reason MUST degrade to in-memory state only, per the capability's
 * "Unavailable storage degrades silently to in-memory state" scenario.
 */
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import type { PeriodGranularity } from '@ooh/contracts/period-granularity';

export type ClientOfferSelectionsScope = 'sso' | 'public';

export interface StoredClientOfferSelections {
  decisions: Record<string, ClientDecision>;
  selectedPeriods: Record<string, string[]>;
  granularity: PeriodGranularity;
}

interface StoredClientOfferSelectionsEnvelope extends StoredClientOfferSelections {
  scope: ClientOfferSelectionsScope;
  scopeId: string;
  versionId: string;
}

function buildStorageKey(
  scope: ClientOfferSelectionsScope,
  scopeId: string,
  versionId: string,
): string {
  return `ooh:client-offer-selections:${scope}:${scopeId}:${versionId}`;
}

/** Narrow, defensive shape check — a malformed/foreign payload is treated as absent. */
function isStoredEnvelope(value: unknown): value is StoredClientOfferSelectionsEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.scope === 'string' &&
    typeof v.scopeId === 'string' &&
    typeof v.versionId === 'string' &&
    typeof v.decisions === 'object' &&
    v.decisions !== null &&
    typeof v.selectedPeriods === 'object' &&
    v.selectedPeriods !== null &&
    typeof v.granularity === 'string'
  );
}

/**
 * Reads a previously stored entry for the given scope/scopeId/versionId.
 * Returns `null` when nothing is stored, the payload is malformed, the
 * scope/scopeId/versionId don't match (a newly published version or a
 * different offer/token), or storage throws for any reason.
 */
export function readStoredClientOfferSelections(
  scope: ClientOfferSelectionsScope,
  scopeId: string,
  versionId: string,
): StoredClientOfferSelections | null {
  try {
    const raw = sessionStorage.getItem(buildStorageKey(scope, scopeId, versionId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredEnvelope(parsed)) return null;
    if (parsed.scope !== scope || parsed.scopeId !== scopeId || parsed.versionId !== versionId) {
      return null;
    }
    return {
      decisions: parsed.decisions,
      selectedPeriods: parsed.selectedPeriods,
      granularity: parsed.granularity,
    };
  } catch {
    return null;
  }
}

/** Writes the current decisions/selectedPeriods/granularity. Silently no-ops on failure. */
export function writeStoredClientOfferSelections(
  scope: ClientOfferSelectionsScope,
  scopeId: string,
  versionId: string,
  entry: StoredClientOfferSelections,
): void {
  try {
    const envelope: StoredClientOfferSelectionsEnvelope = { scope, scopeId, versionId, ...entry };
    sessionStorage.setItem(buildStorageKey(scope, scopeId, versionId), JSON.stringify(envelope));
  } catch {
    // sessionStorage unavailable (private mode, quota) or serialization failed —
    // the in-memory state still works for this session; nothing further to do.
  }
}

/** Removes the stored entry — called after a successful whole-offer Accept. Silently no-ops on failure. */
export function clearStoredClientOfferSelections(
  scope: ClientOfferSelectionsScope,
  scopeId: string,
  versionId: string,
): void {
  try {
    sessionStorage.removeItem(buildStorageKey(scope, scopeId, versionId));
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
